/**
 * PresentationEditor — Visual slide editor with shape canvas
 * Each slide has positioned shapes (rect, ellipse, text, image)
 * Real-time collaboration via Yjs/Hocuspocus
 * Features: Shape rotation, alignment tools, Undo/Redo (Yjs UndoManager),
 *           Export to PowerPoint (.pptx), Slide Numbers toggle
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HocuspocusProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';

import SlideCanvas from './slides/SlideCanvas';
import ShapeToolbar from './slides/ShapeToolbar';
import DictateSlides from './slides/DictateSlides';
import { TEMPLATES, THEMES } from './slides/slideTemplates';
import { exportToPptx } from '../../utils/pptx';

const API = '/api/work/documents';
const COLORS = ['#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];

// Yjs metaMap key for the slide-numbers toggle
const META_SHOW_SLIDE_NUMBERS = 'global:showSlideNumbers';

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

// ──────────────────────────────────────────────────────
// Templates Modal
// ──────────────────────────────────────────────────────
function TemplatesModal({ onClose, onApply }) {
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handleApply = () => {
    if (!selected) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    onApply(selected);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Presentation Templates</h2>
            <p className="text-sm text-gray-500 mt-0.5">Start with a pre-built deck — all slides and layouts included</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Template grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-4">
            {TEMPLATES.map(tpl => (
              <button
                key={tpl.id}
                onClick={() => { setSelected(tpl); setConfirming(false); }}
                className={`text-left rounded-xl border-2 p-4 transition-all hover:shadow-md ${
                  selected?.id === tpl.id
                    ? 'border-indigo-500 bg-indigo-50 shadow-md'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                {/* Slide preview strip */}
                <div className="flex gap-1.5 mb-3 overflow-hidden">
                  {tpl.slides.slice(0, 4).map((slide, i) => (
                    <div
                      key={i}
                      className="flex-1 aspect-[16/9] rounded border border-gray-200 overflow-hidden relative flex-shrink-0"
                      style={{ backgroundColor: slide.bg || '#ffffff', minWidth: 0 }}
                    >
                      {slide.shapes?.slice(0, 3).map((shape, j) => (
                        <div key={j} style={{
                          position: 'absolute',
                          left: `${(shape.x / 960) * 100}%`,
                          top: `${(shape.y / 540) * 100}%`,
                          width: `${(shape.width / 960) * 100}%`,
                          height: `${(shape.height / 540) * 100}%`,
                          backgroundColor: shape.fill === 'transparent' ? 'transparent' : (shape.fill || 'transparent'),
                          borderRadius: shape.type === 'ellipse' ? '50%' : 2,
                          opacity: shape.opacity ?? 1,
                          overflow: 'hidden',
                        }}>
                          {shape.text && (
                            <div style={{ fontSize: 2, color: shape.textColor || '#333', fontWeight: shape.fontWeight, overflow: 'hidden', whiteSpace: 'nowrap', padding: '0.5px' }}>
                              {shape.text.split('\n')[0]}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                  {tpl.slides.length > 4 && (
                    <div className="flex-shrink-0 flex items-center justify-center text-[9px] text-gray-400 font-medium px-1">
                      +{tpl.slides.length - 4}
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-2xl flex-shrink-0">{tpl.icon}</span>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{tpl.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{tpl.desc}</div>
                  </div>
                </div>

                {selected?.id === tpl.id && (
                  <div className="mt-2 flex items-center gap-1 text-indigo-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    <span className="text-xs font-semibold">Selected</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-xs text-gray-500">
            {selected
              ? confirming
                ? <span className="text-amber-600 font-medium">⚠️ This will replace all current slides. Are you sure?</span>
                : `"${selected.label}" — ${selected.slideCount} slides`
              : 'Select a template to get started'}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!selected}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                !selected
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : confirming
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {confirming ? 'Yes, Replace All Slides' : 'Use Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────
// Morph Presenter — Fullscreen presentation with morph transitions
// ──────────────────────────────────────────────────────
const SLIDE_W = 960;
const SLIDE_H = 540;
const DEFAULT_MORPH_DURATION = 800;

function getShapesArray(getShapesMap, slideId) {
  const map = getShapesMap(slideId);
  const arr = [];
  map.forEach((val) => { if (typeof val === 'object' && val.id) arr.push(val); });
  arr.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  return arr;
}

function MorphPresenter({ slides, activeSlide, getShapesMap, onNext, showSlideNumbers }) {
  const [prevSlideIdx, setPrevSlideIdx] = useState(activeSlide);
  const [morphing, setMorphing] = useState(false);
  const [morphShapes, setMorphShapes] = useState([]);
  const [displayShapes, setDisplayShapes] = useState([]);
  const [bgColor, setBgColor] = useState(slides[activeSlide]?.bg || '#ffffff');
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setScale(Math.min(r.width / SLIDE_W, r.height / SLIDE_H));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (morphing) return;
    const sid = slides[activeSlide]?.id;
    if (sid) setDisplayShapes(getShapesArray(getShapesMap, sid));
    setBgColor(slides[activeSlide]?.bg || '#ffffff');
  }, [activeSlide, morphing, slides, getShapesMap]);

  const morphDuration = slides[activeSlide]?.transitionDuration || DEFAULT_MORPH_DURATION;

  useEffect(() => {
    if (activeSlide === prevSlideIdx) return;
    const currentSlide = slides[activeSlide];
    const prevSlide = slides[prevSlideIdx];
    const morphDuration = currentSlide?.transitionDuration || DEFAULT_MORPH_DURATION;

    if (currentSlide?.transition === 'morph' && prevSlide) {
      const prevShapes = getShapesArray(getShapesMap, prevSlide.id);
      const currShapes = getShapesArray(getShapesMap, currentSlide.id);

      const morphKey = (s) => s.id.replace(/^shape_\d+_/, 'shape_');
      const contentKey = (s) => `${s.type}:${(s.text || '').trim().substring(0, 30)}`;

      const prevByKey = {};
      const prevByContent = {};
      for (const s of prevShapes) {
        prevByKey[morphKey(s)] = s;
        if (s.text?.trim()) prevByContent[contentKey(s)] = s;
      }

      const matched = new Set();
      const morphList = [];

      for (const to of currShapes) {
        const key = morphKey(to);
        const from = prevByKey[key];
        if (from && !matched.has(from.id)) {
          morphList.push({ id: to.id, from, to, type: 'morph' });
          matched.add(from.id);
          matched.add(to.id);
        }
      }

      for (const to of currShapes) {
        if (matched.has(to.id)) continue;
        const key = contentKey(to);
        const from = prevByContent[key];
        if (from && !matched.has(from.id)) {
          morphList.push({ id: to.id, from, to, type: 'morph' });
          matched.add(from.id);
          matched.add(to.id);
        }
      }

      for (const s of prevShapes) {
        if (!matched.has(s.id)) morphList.push({ id: s.id, from: s, to: { ...s, opacity: 0 }, type: 'exit' });
      }
      for (const s of currShapes) {
        if (!matched.has(s.id)) morphList.push({ id: `enter_${s.id}`, from: { ...s, opacity: 0 }, to: s, type: 'enter' });
      }

      morphedShapeIdsRef.current = new Set(morphList.map(m => m.id));
      setMorphShapes(morphList.map(m => ({ ...m, current: m.from })));
      setBgColor(prevSlide.bg || '#ffffff');
      setMorphing(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMorphShapes(morphList.map(m => ({ ...m, current: m.to })));
          setBgColor(currentSlide.bg || '#ffffff');
        });
      });

      const timer = setTimeout(() => {
        setMorphing(false);
        setPrevSlideIdx(activeSlide);
      }, morphDuration + 50);
      return () => clearTimeout(timer);
    } else if (currentSlide?.transition === 'fade' && prevSlide) {
      const prevShapes = getShapesArray(getShapesMap, prevSlide.id);
      const currShapes = getShapesArray(getShapesMap, currentSlide.id);
      const morphList = [
        ...prevShapes.map(s => ({ id: `exit_${s.id}`, from: s, to: { ...s, opacity: 0 }, type: 'exit' })),
        ...currShapes.map(s => ({ id: `enter_${s.id}`, from: { ...s, opacity: 0 }, to: s, type: 'enter' })),
      ];
      setMorphShapes(morphList.map(m => ({ ...m, current: m.from })));
      setBgColor(prevSlide.bg || '#ffffff');
      setMorphing(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMorphShapes(morphList.map(m => ({ ...m, current: m.to })));
          setBgColor(currentSlide.bg || '#ffffff');
        });
      });
      const timer = setTimeout(() => { setMorphing(false); setPrevSlideIdx(activeSlide); }, morphDuration + 50);
      return () => clearTimeout(timer);
    } else {
      setPrevSlideIdx(activeSlide);
    }
  }, [activeSlide]);

  const [animatedShapeIds, setAnimatedShapeIds] = useState(new Set());
  const [animationPhase, setAnimationPhase] = useState('idle');
  const morphedShapeIdsRef = useRef(new Set());

  useEffect(() => {
    if (morphing) return;
    const hadMorph = morphedShapeIdsRef.current.size > 0;
    if (hadMorph) {
      setAnimatedShapeIds(new Set(displayShapes.map(s => s.id)));
      morphedShapeIdsRef.current = new Set();
      return;
    }
    const currentShapes = displayShapes.filter(s => s.animation && s.animation !== 'none');
    if (currentShapes.length === 0) {
      setAnimatedShapeIds(new Set(displayShapes.map(s => s.id)));
      return;
    }
    const noAnimation = new Set(displayShapes.filter(s => !s.animation || s.animation === 'none').map(s => s.id));
    setAnimatedShapeIds(noAnimation);
    setAnimationPhase('running');
    const timers = [];
    for (const shape of currentShapes) {
      const delay = (shape.animationDelay || 0) * 1000;
      const t = setTimeout(() => {
        setAnimatedShapeIds(prev => new Set([...prev, shape.id]));
      }, delay);
      timers.push(t);
    }
    const maxDelay = Math.max(...currentShapes.map(s => ((s.animationDelay || 0) * 1000) + (s.animationDuration || 500)));
    const endTimer = setTimeout(() => setAnimationPhase('idle'), maxDelay + 100);
    timers.push(endTimer);
    return () => timers.forEach(clearTimeout);
  }, [activeSlide, morphing, displayShapes]);

  const getAnimationStyle = (shape) => {
    const isVisible = animatedShapeIds.has(shape.id);
    const anim = shape.animation || 'none';
    const dur = shape.animationDuration || 500;
    if (anim === 'none') return {};
    if (!isVisible) {
      const hidden = { opacity: 0, transition: 'none' };
      if (anim === 'slideInLeft') hidden.transform = 'translateX(-60px)';
      if (anim === 'slideInRight') hidden.transform = 'translateX(60px)';
      if (anim === 'slideInUp') hidden.transform = 'translateY(60px)';
      if (anim === 'slideInDown') hidden.transform = 'translateY(-60px)';
      if (anim === 'scaleIn') hidden.transform = 'scale(0.3)';
      if (anim === 'bounceIn') hidden.transform = 'scale(0)';
      return hidden;
    }
    const easing = anim === 'bounceIn' ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)';
    return {
      opacity: shape.opacity ?? 1,
      transform: 'none',
      transition: `opacity ${dur}ms ${easing}, transform ${dur}ms ${easing}`,
    };
  };

  const shapesToRender = morphing ? morphShapes : displayShapes.map(s => ({ id: s.id, current: s, type: 'static' }));

  return (
    <div ref={containerRef}
      className="fixed inset-0 bg-black flex items-center justify-center z-[999] cursor-pointer select-none"
      onClick={onNext}>
      <div className="relative shadow-2xl overflow-hidden"
        style={{
          width: SLIDE_W * scale,
          height: SLIDE_H * scale,
          backgroundColor: bgColor,
          transition: morphing ? `background-color ${morphDuration}ms ease` : 'none',
        }}>
        <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative' }}>
          {shapesToRender.map(({ id, current, type }) => {
            const animStyle = !morphing ? getAnimationStyle(current) : {};
            return (
              <div key={id} style={{
                position: 'absolute',
                left: current.x,
                top: current.y,
                width: current.width,
                height: current.height,
                backgroundColor: current.type === 'image' ? 'transparent' : (current.fill || 'transparent'),
                border: current.stroke ? `${current.strokeWidth || 1}px solid ${current.stroke}` : 'none',
                borderRadius: current.type === 'ellipse' ? '50%' : (current.radius || 0),
                opacity: current.opacity ?? 1,
                zIndex: current.zIndex || 0,
                transform: current.rotation ? `rotate(${current.rotation}deg)` : undefined,
                transformOrigin: 'center center',
                display: 'flex', alignItems: 'center',
                justifyContent: current.textAlign === 'left' ? 'flex-start' : current.textAlign === 'right' ? 'flex-end' : 'center',
                overflow: 'hidden',
                transition: morphing ? `all ${morphDuration}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
                ...animStyle,
              }}>
                {current.type === 'text' && (
                  <span style={{
                    color: current.textColor || '#1F2937',
                    fontSize: current.fontSize || 24,
                    fontWeight: current.fontWeight || 'normal',
                    fontStyle: current.fontStyle || 'normal',
                    fontFamily: current.fontFamily || 'inherit',
                    textAlign: current.textAlign || 'center',
                    padding: '4px 8px',
                    width: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    transition: morphing ? `all ${morphDuration}ms ease` : 'none',
                  }}>{current.text}</span>
                )}
                {current.type === 'image' && (current.src || current.storageKey) && (
                  <img src={current.storageKey ? `/api/work/documents/image/${current.storageKey}` : current.src}
                    alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}
              </div>
            );
          })}

          {/* Slide number overlay — bottom-right corner */}
          {showSlideNumbers && (
            <div style={{
              position: 'absolute',
              bottom: 16,
              right: 20,
              fontSize: 14,
              color: 'rgba(0,0,0,0.35)',
              fontFamily: 'inherit',
              pointerEvents: 'none',
              zIndex: 9999,
              userSelect: 'none',
            }}>
              {activeSlide + 1} / {slides.length}
            </div>
          )}
        </div>
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm pointer-events-none">
        {activeSlide + 1} / {slides.length} — Press Esc to exit
      </div>
    </div>
  );
}

// Mini slide thumbnail
function SlideThumbnail({ slideId, bg, getShapesMap, index }) {
  const [thumbShapes, setThumbShapes] = useState([]);
  useEffect(() => {
    const map = getShapesMap(slideId);
    const load = () => {
      const arr = [];
      map.forEach((v) => { if (v?.id) arr.push(v); });
      setThumbShapes(arr);
    };
    load();
    map.observe(load);
    return () => map.unobserve(load);
  }, [slideId, getShapesMap]);

  return (
    <div className="aspect-[16/9] rounded overflow-hidden relative" style={{ backgroundColor: bg || '#fff' }}>
      {thumbShapes.map(s => (
        <div key={s.id} style={{
          position: 'absolute',
          left: `${(s.x / 960) * 100}%`, top: `${(s.y / 540) * 100}%`,
          width: `${(s.width / 960) * 100}%`, height: `${(s.height / 540) * 100}%`,
          backgroundColor: s.fill || 'transparent',
          borderRadius: s.type === 'ellipse' ? '50%' : 2,
          opacity: s.opacity ?? 1,
          overflow: 'hidden',
        }}>
          {s.type === 'image' && (s.storageKey || s.src) ? (
            <img src={s.storageKey ? `/api/work/documents/image/${s.storageKey}` : s.src} alt="" className="w-full h-full object-cover" />
          ) : s.text ? (
            <div className="text-[3px] leading-tight p-[1px] truncate" style={{ color: s.textColor || '#333' }}>{s.text.split('\n')[0]}</div>
          ) : null}
        </div>
      ))}
      {thumbShapes.length === 0 && <div className="text-[6px] text-gray-400 p-1">Slide {index + 1}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────
// Undo/Redo button component
// ──────────────────────────────────────────────────────
function UndoRedoButtons({ undoManager }) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    if (!undoManager) return;
    const update = () => {
      setCanUndo(undoManager.canUndo());
      setCanRedo(undoManager.canRedo());
    };
    update();
    undoManager.on('stack-item-added', update);
    undoManager.on('stack-item-popped', update);
    undoManager.on('stack-cleared', update);
    return () => {
      undoManager.off('stack-item-added', update);
      undoManager.off('stack-item-popped', update);
      undoManager.off('stack-cleared', update);
    };
  }, [undoManager]);

  return (
    <div className="flex items-center gap-0.5">
      <button
        onClick={() => undoManager?.undo()}
        disabled={!canUndo}
        title="Undo (Cmd+Z)"
        className={`p-1.5 rounded transition-colors ${
          canUndo ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 010 10H9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10l4-4M3 10l4 4" />
        </svg>
      </button>
      <button
        onClick={() => undoManager?.redo()}
        disabled={!canRedo}
        title="Redo (Cmd+Shift+Z)"
        className={`p-1.5 rounded transition-colors ${
          canRedo ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a5 5 0 000 10h4" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 10l-4-4M21 10l-4 4" />
        </svg>
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────
// freshTemplate — deep-clone a template with new IDs
// so re-applying the same template never collides with
// stale Yjs map keys from a previous application.
// ──────────────────────────────────────────────────────
function freshTemplate(template) {
  const now = Date.now();
  return {
    ...template,
    slides: template.slides.map((slide, si) => {
      const newSlideId = `s_${now}_${si}_${Math.random().toString(36).slice(2, 8)}`;
      return {
        ...slide,
        id: newSlideId,
        shapes: (slide.shapes || []).map((shape, shi) => ({
          ...shape,
          id: `shape_${now}_${si}_${shi}_${Math.random().toString(36).slice(2, 6)}`,
          zIndex: shape.zIndex ?? (shi + 1) * 10,
        })),
      };
    }),
  };
}

export default function PresentationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [saved, setSaved] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedShapeIds, setSelectedShapeIds] = useState(new Set()); // multi-select
  const [selectedSlideIds, setSelectedSlideIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [presenting, setPresenting] = useState(false);
  const [speakerNotes, setSpeakerNotes] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [exporting, setExporting] = useState(false);
  // ─── Slide numbers toggle — persisted in Yjs metaMap ───────────────────────
  const [showSlideNumbers, setShowSlideNumbers] = useState(false);
  const pendingImportRef = useRef(null);

  const ydoc = useMemo(() => new Y.Doc(), []);
  const slidesArray = useMemo(() => ydoc.getArray('slides'), [ydoc]);
  const notesMap = useMemo(() => ydoc.getMap('notes'), [ydoc]);
  const metaMap = useMemo(() => ydoc.getMap('slideMeta'), [ydoc]);

  // ─── Yjs UndoManager ───────────────────────────────────────────────────────
  const undoManager = useMemo(() => {
    const um = new Y.UndoManager([], { doc: ydoc, captureTimeout: 500 });
    return um;
  }, [ydoc]);

  const getShapesMap = useCallback((slideId) => {
    return ydoc.getMap(`shapes:${slideId}`);
  }, [ydoc]);

  // Register shape maps with undo manager when slides change
  useEffect(() => {
    if (!undoManager || slides.length === 0) return;
    for (const slide of slides) {
      const map = getShapesMap(slide.id);
      undoManager.addToScope(map);
    }
    undoManager.addToScope(slidesArray);
    undoManager.addToScope(metaMap);
  }, [slides, undoManager, getShapesMap, slidesArray, metaMap]);

  // ─── Sync showSlideNumbers from metaMap ────────────────────────────────────
  // Read the initial value and observe future changes (e.g. from other collaborators)
  useEffect(() => {
    const syncFlag = () => {
      setShowSlideNumbers(!!metaMap.get(META_SHOW_SLIDE_NUMBERS));
    };
    syncFlag();
    metaMap.observe(syncFlag);
    return () => metaMap.unobserve(syncFlag);
  }, [metaMap]);

  // Toggle handler — writes to Yjs so all collaborators see the change
  const handleToggleSlideNumbers = useCallback(() => {
    const next = !metaMap.get(META_SHOW_SLIDE_NUMBERS);
    metaMap.set(META_SHOW_SLIDE_NUMBERS, next);
    // setShowSlideNumbers is driven by the observer above, no need to call it here
  }, [metaMap]);

  const provider = useMemo(() => {
    if (!id) return null;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return new HocuspocusProvider({
      url: `${wsProtocol}//${window.location.host}/collaboration`,
      name: `doc:${id}`, document: ydoc,
      token: getCookie('session_token') || 'none',
      onSynced: () => {
        setSaved(true);
        syncSlides();
        setTimeout(() => {
          const ic = pendingImportRef.current;
          if (ic?.slides?.length > 0 && slidesArray.length <= 1) {
            if (slidesArray.length > 0) slidesArray.delete(0, slidesArray.length);
            ydoc.transact(() => {
              for (const slide of ic.slides) {
                slidesArray.push([slide.id]);
                metaMap.set(`${slide.id}:bg`, slide.bg || '#ffffff');
                if (slide.transition) metaMap.set(`${slide.id}:transition`, slide.transition);
                if (slide.notes) metaMap.set(`${slide.id}:notes`, slide.notes);
                if (slide.shapes) {
                  const shapeList = Array.isArray(slide.shapes) ? slide.shapes : Object.values(slide.shapes);
                  const map = getShapesMap(slide.id);
                  for (const shape of shapeList) map.set(shape.id, shape);
                }
              }
            });
            pendingImportRef.current = null;
            syncSlides();
            fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ settings: {} }) }).catch(() => {});
          } else if (slidesArray.length === 0) {
            addSlide();
          }
        }, 500);
      },
      onAwarenessUpdate: ({ states }) => {
        const users = [];
        states.forEach(s => { if (s.user) users.push(s.user); });
        setConnectedUsers(users);
      },
    });
  }, [id, ydoc]);

  const syncSlides = useCallback(() => {
    const ids = slidesArray.toArray();
    setSlides(ids.map((slideId, i) => ({
      id: slideId, index: i,
      bg: metaMap.get(`${slideId}:bg`) || '#ffffff',
      transition: metaMap.get(`${slideId}:transition`) || 'none',
      transitionDuration: metaMap.get(`${slideId}:transitionDuration`) || 800,
    })));
    if (ids[activeSlide]) {
      syncShapes(ids[activeSlide]);
      setSpeakerNotes(notesMap.get(ids[activeSlide]) || '');
    }
  }, [slidesArray, metaMap, notesMap, activeSlide]);

  const syncShapes = useCallback((slideId) => {
    const map = getShapesMap(slideId);
    const arr = [];
    map.forEach((val) => {
      if (typeof val === 'object' && val.id) arr.push(val);
    });
    arr.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    setShapes(arr);
  }, [getShapesMap]);

  useEffect(() => {
    const fonts = new Set();
    for (const slide of slides) {
      const map = getShapesMap(slide.id);
      map.forEach(shape => {
        if (shape?.fontFamily && !shape.fontFamily.startsWith('+')) fonts.add(shape.fontFamily);
      });
    }
    if (fonts.size === 0) return;
    const loadedFonts = new Set(document.querySelectorAll('link[data-gfont]'));
    const loadedNames = new Set([...loadedFonts].map(l => l.dataset.gfont));
    for (const font of fonts) {
      if (loadedNames.has(font)) continue;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;700&display=swap`;
      link.dataset.gfont = font;
      document.head.appendChild(link);
    }
  }, [slides, shapes]);

  useEffect(() => {
    const slidesObs = () => syncSlides();
    slidesArray.observe(slidesObs);
    metaMap.observe(slidesObs);
    return () => { slidesArray.unobserve(slidesObs); metaMap.unobserve(slidesObs); };
  }, [slidesArray, metaMap, syncSlides]);

  useEffect(() => {
    const slideId = slides[activeSlide]?.id;
    if (!slideId) return;
    const map = getShapesMap(slideId);
    const obs = () => syncShapes(slideId);
    map.observe(obs);
    syncShapes(slideId);
    setSpeakerNotes(notesMap.get(slideId) || '');
    return () => map.unobserve(obs);
  }, [activeSlide, slides, getShapesMap, syncShapes, notesMap]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/${id}`, { credentials: 'include' });
        const data = await res.json();
        setDoc(data.document);
        setTitle(data.document?.title || 'Untitled Presentation');

        const ic = data.document?.settings?.initialContent;
        if (ic?.slides?.length > 0) {
          pendingImportRef.current = ic;
          if (slidesArray.length <= 1) {
            setTimeout(() => {
              if (!pendingImportRef.current) return;
              if (slidesArray.length > 0) slidesArray.delete(0, slidesArray.length);
              ydoc.transact(() => {
                for (const slide of ic.slides) {
                  slidesArray.push([slide.id]);
                  metaMap.set(`${slide.id}:bg`, slide.bg || '#ffffff');
                  if (slide.transition) metaMap.set(`${slide.id}:transition`, slide.transition);
                  if (slide.notes) metaMap.set(`${slide.id}:notes`, slide.notes);
                  if (slide.shapes) {
                    const shapeList = Array.isArray(slide.shapes) ? slide.shapes : Object.values(slide.shapes);
                    const map = getShapesMap(slide.id);
                    for (const shape of shapeList) map.set(shape.id, shape);
                  }
                }
              });
              pendingImportRef.current = null;
              syncSlides();
              fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: {} }) }).catch(() => {});
            }, 500);
          }
        }
      } catch { navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work'); }
      finally { setLoading(false); }
    })();
    return () => { provider?.destroy(); ydoc.destroy(); };
  }, [id]);

  // ─── Export to PowerPoint ──────────────────────────────────────────────────
  const handleExportPptx = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Build a plain-JS snapshot of every slide + its shapes from Yjs
      const slideSnapshots = slides.map(slide => {
        const shapesMap = getShapesMap(slide.id);
        const shapeList = [];
        shapesMap.forEach((val) => {
          if (typeof val === 'object' && val.id) shapeList.push(val);
        });
        shapeList.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

        return {
          id: slide.id,
          background: slide.bg || '#ffffff',
          shapes: shapeList.map(s => ({
            type:        s.type,
            x:           s.x ?? 0,
            y:           s.y ?? 0,
            w:           s.width  ?? s.w  ?? 100,
            h:           s.height ?? s.h  ?? 60,
            text:        s.text   ?? '',
            fontSize:    s.fontSize    ?? 18,
            bold:        s.fontWeight === 'bold' || s.bold === true,
            italic:      s.fontStyle  === 'italic' || s.italic === true,
            color:       s.textColor  ?? s.color ?? '#000000',
            fill:        s.fill       ?? 'transparent',
            borderColor: s.stroke     ?? s.borderColor ?? null,
            borderWidth: s.strokeWidth ?? s.borderWidth ?? 1,
            rotation:    s.rotation   ?? 0,
            opacity:     s.opacity    ?? 1,
            align:       s.textAlign  ?? 'center',
            src:         s.storageKey
                           ? `${window.location.origin}/api/work/documents/image/${s.storageKey}`
                           : (s.src ?? null),
          })),
        };
      });

      await exportToPptx(slideSnapshots, title || 'Presentation');
    } catch (err) {
      console.error('[Export PPTX] Failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [slides, getShapesMap, title, exporting]);

  // Shape operations
  const addShape = (shape) => {
    const slideId = slides[activeSlide]?.id;
    if (!slideId) return;
    const map = getShapesMap(slideId);
    map.set(shape.id, shape);
    setSelectedShapeId(shape.id);
    setSelectedShapeIds(new Set([shape.id]));
    setSaved(false);
  };

  const updateShape = (shapeId, updates) => {
    const slideId = slides[activeSlide]?.id;
    if (!slideId) return;
    const map = getShapesMap(slideId);
    const existing = map.get(shapeId);
    if (existing) {
      map.set(shapeId, { ...existing, ...updates });
      setSaved(false);
    }
  };

  const deleteShape = (shapeId) => {
    const slideId = slides[activeSlide]?.id;
    if (!slideId) return;
    const map = getShapesMap(slideId);
    map.delete(shapeId);
    if (selectedShapeId === shapeId) {
      setSelectedShapeId(null);
      setSelectedShapeIds(new Set());
    }
    setSaved(false);
  };

  // Slide operations
  const addSlide = (index) => {
    const slideId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const insertAt = index !== undefined ? index + 1 : slidesArray.length;
    slidesArray.insert(insertAt, [slideId]);
    metaMap.set(`${slideId}:bg`, '#ffffff');
    setActiveSlide(insertAt);
  };

  const copySlides = (slideIndices) => {
    const copied = slideIndices.map(i => {
      const slide = slides[i];
      if (!slide) return null;
      const shapesMap = getShapesMap(slide.id);
      const shapeList = [];
      shapesMap.forEach((val) => { if (typeof val === 'object' && val.id) shapeList.push(val); });
      return {
        bg: slide.bg, transition: slide.transition, transitionDuration: slide.transitionDuration,
        notes: notesMap.get(slide.id) || '',
        shapes: shapeList,
      };
    }).filter(Boolean);
    if (copied.length > 0) {
      localStorage.setItem('jubly_slide_clipboard', JSON.stringify({ slides: copied, copiedAt: Date.now() }));
    }
    return copied.length;
  };

  const pasteSlides = (afterIndex) => {
    try {
      const data = JSON.parse(localStorage.getItem('jubly_slide_clipboard') || '{}');
      if (!data.slides?.length) return 0;
      const insertAt = afterIndex !== undefined ? afterIndex + 1 : slidesArray.length;
      ydoc.transact(() => {
        for (let j = 0; j < data.slides.length; j++) {
          const src = data.slides[j];
          const newId = `s_${Date.now()}_${j}_${Math.random().toString(36).slice(2, 6)}`;
          slidesArray.insert(insertAt + j, [newId]);
          metaMap.set(`${newId}:bg`, src.bg || '#ffffff');
          if (src.transition) metaMap.set(`${newId}:transition`, src.transition);
          if (src.transitionDuration) metaMap.set(`${newId}:transitionDuration`, src.transitionDuration);
          if (src.notes) notesMap.set(newId, src.notes);
          const newMap = getShapesMap(newId);
          for (const shape of src.shapes || []) {
            const newShape = { ...shape, id: `${shape.id}_copy_${Date.now()}` };
            newMap.set(newShape.id, newShape);
          }
        }
      });
      setActiveSlide(insertAt);
      syncSlides();
      return data.slides.length;
    } catch { return 0; }
  };

  const importPendingSlides = useCallback(async () => {
    try {
      const res = await fetch(`${API}/${id}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const pending = data.document?.settings?.pendingSlides;
      if (!pending?.slides?.length) return;

      const newTitle = data.document?.title;
      if (newTitle && newTitle !== 'Untitled Presentation' && newTitle !== title) {
        setTitle(newTitle);
      }

      ydoc.transact(() => {
        const existingCount = slidesArray.length;
        let allEmpty = true;
        for (let i = 0; i < existingCount; i++) {
          const sid = slidesArray.get(i);
          const shapes = getShapesMap(sid);
          let count = 0;
          shapes.forEach(() => count++);
          if (count > 2) { allEmpty = false; break; }
        }
        if (allEmpty && existingCount <= 3) {
          for (let i = existingCount - 1; i >= 0; i--) slidesArray.delete(i, 1);
        }

        for (const slide of pending.slides) {
          const slideId = slide.id || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          slidesArray.push([slideId]);
          metaMap.set(`${slideId}:bg`, slide.bg || slide.bgColor || '#ffffff');
          if (slide.transition) metaMap.set(`${slideId}:transition`, slide.transition);
          if (slide.transitionDuration) metaMap.set(`${slideId}:transitionDuration`, slide.transitionDuration);
          if (slide.notes) notesMap.set(slideId, slide.notes);

          const shapesMap = getShapesMap(slideId);
          const shapeList = Array.isArray(slide.shapes) ? slide.shapes : Object.values(slide.shapes || {});
          for (const shape of shapeList) {
            if (shape?.id) shapesMap.set(shape.id, shape);
          }
        }
      });

      await fetch(`${API}/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { ...data.document.settings, pendingSlides: null } }),
      });

      setSaved(false);
    } catch (err) {
      console.error('Failed to import pending slides:', err);
    }
  }, [id, ydoc, slidesArray, metaMap, notesMap, getShapesMap]);

  const deleteSlide = (index) => {
    if (slidesArray.length <= 1) return;
    slidesArray.delete(index, 1);
    const newLen = slidesArray.length;
    if (activeSlide >= newLen) setActiveSlide(Math.max(0, newLen - 1));
    syncSlides();
  };

  const updateNotes = (text) => {
    setSpeakerNotes(text);
    if (slides[activeSlide]) notesMap.set(slides[activeSlide].id, text);
  };

  async function handleTitleChange(t) {
    setTitle(t);
    try {
      await fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: t }) });
    } catch {}
  }

  // Apply Layout
  const applyLayout = useCallback((layout) => {
    const slideId = slides[activeSlide]?.id;
    if (!slideId) return;
    ydoc.transact(() => {
      const map = getShapesMap(slideId);
      const keysToDelete = [];
      map.forEach((_, key) => keysToDelete.push(key));
      keysToDelete.forEach(k => map.delete(k));
      if (layout.bg) metaMap.set(`${slideId}:bg`, layout.bg);
      layout.shapes.forEach((shape, i) => {
        const newShape = {
          ...shape,
          id: `shape_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
          zIndex: (i + 1) * 10,
        };
        map.set(newShape.id, newShape);
      });
    });
    setSelectedShapeId(null);
    setSelectedShapeIds(new Set());
    setSaved(false);
    syncSlides();
  }, [slides, activeSlide, ydoc, getShapesMap, metaMap, syncSlides]);

  // Apply Theme
  const applyTheme = useCallback((theme) => {
    ydoc.transact(() => {
      for (const slide of slides) {
        metaMap.set(`${slide.id}:bg`, theme.bg);
        const map = getShapesMap(slide.id);
        const updates = [];
        map.forEach((shape, key) => {
          if (shape?.type === 'text') {
            updates.push([key, { ...shape, textColor: theme.textColor }]);
          } else if (shape?.type === 'rect' && shape.fill && shape.fill !== 'transparent') {
            const isAccent = !['#ffffff', '#f3f4f6', '#f9fafb', '#e5e7eb', '#d1d5db', 'transparent'].includes(shape.fill.toLowerCase());
            if (isAccent) updates.push([key, { ...shape, fill: theme.accentColor }]);
          }
        });
        updates.forEach(([key, val]) => map.set(key, val));
      }
    });
    setSaved(false);
    syncSlides();
  }, [slides, ydoc, metaMap, getShapesMap, syncSlides]);

  // ──────────────────────────────────────────────────────
  // Apply Template
  // ──────────────────────────────────────────────────────
  const applyTemplate = useCallback((template) => {
    const fresh = freshTemplate(template);

    ydoc.transact(() => {
      const existingCount = slidesArray.length;
      for (let i = existingCount - 1; i >= 0; i--) {
        slidesArray.delete(i, 1);
      }

      for (const slide of fresh.slides) {
        slidesArray.push([slide.id]);
        metaMap.set(`${slide.id}:bg`, slide.bg || '#ffffff');
        if (slide.transition) metaMap.set(`${slide.id}:transition`, slide.transition);
        if (slide.transitionDuration) metaMap.set(`${slide.id}:transitionDuration`, slide.transitionDuration);
        if (slide.notes) notesMap.set(slide.id, slide.notes);

        const map = getShapesMap(slide.id);
        for (const shape of slide.shapes) {
          map.set(shape.id, shape);
        }
      }
    });

    setActiveSlide(0);
    setSelectedShapeId(null);
    setSelectedShapeIds(new Set());
    setSelectedSlideIds(new Set());
    setSaved(false);

    syncSlides();
  }, [ydoc, slidesArray, metaMap, notesMap, getShapesMap, syncSlides]);

  // ─── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const active = document.activeElement?.tagName;
      const isInput = active === 'INPUT' || active === 'TEXTAREA';

      // Undo/Redo — always active (even in inputs, Cmd+Z is expected)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (undoManager?.canUndo()) {
          e.preventDefault();
          undoManager.undo();
          return;
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        if (undoManager?.canRedo()) {
          e.preventDefault();
          undoManager.redo();
          return;
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        if (undoManager?.canRedo()) {
          e.preventDefault();
          undoManager.redo();
          return;
        }
      }

      if (isInput) return;

      if (presenting) {
        if (e.key === 'ArrowRight' || e.key === ' ') setActiveSlide(s => Math.min(s + 1, slides.length - 1));
        else if (e.key === 'ArrowLeft') setActiveSlide(s => Math.max(s - 1, 0));
        else if (e.key === 'Escape') { setPresenting(false); document.exitFullscreen?.(); }
      } else {
        if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); setActiveSlide(s => Math.min(s + 1, slides.length - 1)); setSelectedShapeId(null); }
        else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); setActiveSlide(s => Math.max(s - 1, 0)); setSelectedShapeId(null); }
        else if ((e.metaKey || e.ctrlKey) && e.key === 'c' && (selectedSlideIds.size > 0 || !selectedShapeId)) {
          e.preventDefault();
          const indices = selectedSlideIds.size > 0
            ? [...selectedSlideIds].map(sid => slides.findIndex(s => s.id === sid)).filter(i => i >= 0)
            : [activeSlide];
          copySlides(indices);
        }
        else if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
          if (localStorage.getItem('jubly_slide_clipboard')) {
            e.preventDefault();
            pasteSlides(activeSlide);
          }
        }
        // Delete selected shape
        else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapeId) {
          e.preventDefault();
          deleteShape(selectedShapeId);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [presenting, slides.length, selectedShapeId, selectedSlideIds, activeSlide, undoManager]);

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-orange-500" />
    </div>;
  }

  if (presenting) {
    return (
      <MorphPresenter
        slides={slides}
        activeSlide={activeSlide}
        getShapesMap={getShapesMap}
        onNext={() => setActiveSlide(s => Math.min(s + 1, slides.length - 1))}
        showSlideNumbers={showSlideNumbers}
      />
    );
  }

  const selectedShape = shapes.find(s => s.id === selectedShapeId);
  // Build array of all selected shapes for alignment tools
  const selectedShapesArr = selectedShapeIds.size > 1
    ? shapes.filter(s => selectedShapeIds.has(s.id))
    : selectedShape ? [selectedShape] : [];

  return (
    <div className="flex h-screen flex-col bg-gray-100">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-4 py-2 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={() => navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work')} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 flex-shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <input type="text" value={title} onChange={e => handleTitleChange(e.target.value)}
            style={{ minWidth: Math.min(Math.max(title.length * 10, 200), 600) }}
            className="border-none bg-transparent text-base sm:text-lg font-semibold text-gray-900 focus:outline-none min-w-0" placeholder="Untitled Presentation" />
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Undo/Redo buttons */}
          <UndoRedoButtons undoManager={undoManager} />

          <div className="w-px h-5 bg-gray-200 mx-0.5" />

          {/* Export to PowerPoint button */}
          <button
            onClick={handleExportPptx}
            disabled={exporting}
            title="Export to PowerPoint (.pptx)"
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              exporting
                ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            {exporting ? (
              <>
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                Exporting…
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 12l-4-4m4 4l4-4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h16" />
                </svg>
                Export .pptx
              </>
            )}
          </button>

          <div className="w-px h-5 bg-gray-200 mx-0.5" />

          {/* Templates button */}
          <button
            onClick={() => setShowTemplates(true)}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
            Templates
          </button>

          <DictateSlides documentId={id} onSlidesReady={importPendingSlides} />
          <button onClick={() => { setPresenting(true); document.documentElement.requestFullscreen?.(); }}
            className="px-2 sm:px-3 py-1.5 bg-orange-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-orange-700 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /></svg>
            <span className="hidden sm:inline">Present</span>
          </button>
          <div className="flex -space-x-2">
            {connectedUsers.slice(0, 3).map((u, i) => (
              <div key={i} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-white text-[10px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: u.color || COLORS[i % COLORS.length] }}>{(u.name||'?')[0].toUpperCase()}</div>
            ))}
          </div>
          <span className={`text-xs hidden sm:inline ${saved ? 'text-gray-400' : 'text-amber-500'}`}>{saved ? 'Saved' : 'Saving...'}</span>
        </div>
      </header>

      {/* Shape toolbar */}
      <ShapeToolbar
        selectedShape={selectedShape}
        selectedShapes={selectedShapesArr}
        onAdd={addShape}
        onUpdate={updateShape}
        onDelete={deleteShape}
        currentSlide={slides[activeSlide]}
        slides={slides}
        allShapes={shapes}
        onApplyLayout={applyLayout}
        onApplyTheme={applyTheme}
        showSlideNumbers={showSlideNumbers}
        onToggleSlideNumbers={handleToggleSlideNumbers}
        onSlideUpdate={(key, value, slideId) => {
          const sid = slideId || slides[activeSlide]?.id;
          if (!sid) return;
          metaMap.set(`${sid}:${key}`, value);
          syncSlides();
        }}
      />

      {/* Mobile slide thumbnails */}
      <div className="sm:hidden flex items-center gap-2 px-2 py-1.5 bg-white border-b border-gray-200 overflow-x-auto flex-shrink-0">
        {slides.map((slide, i) => (
          <button key={slide.id} onClick={() => { setActiveSlide(i); setSelectedShapeId(null); }}
            className={`flex-shrink-0 w-16 h-10 rounded border-2 overflow-hidden relative ${activeSlide === i ? 'border-orange-500 shadow-sm' : 'border-gray-200'}`}
            style={{ backgroundColor: slide.bg || '#fff' }}>
            <SlideThumbnail slideId={slide.id} bg={slide.bg} getShapesMap={getShapesMap} index={i} />
            <span className="absolute top-0 left-0.5 text-[7px] text-gray-400 font-medium">{i + 1}</span>
            {slide.transition && slide.transition !== 'none' && (
              <span className={`absolute bottom-0 left-0.5 text-[6px] px-0.5 rounded ${slide.transition === 'morph' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {slide.transition === 'morph' ? 'M' : 'F'}
              </span>
            )}
          </button>
        ))}
        <button onClick={() => addSlide(activeSlide)} className="flex-shrink-0 w-10 h-10 border-2 border-dashed border-gray-300 rounded text-gray-400 text-lg hover:border-gray-400">+</button>
        <span className="flex-shrink-0 text-[10px] text-gray-400 pl-1">{activeSlide + 1}/{slides.length}</span>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Slide panel — desktop */}
        <div className="hidden sm:block w-48 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0 p-2 space-y-1.5">
          {selectedSlideIds.size > 1 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 space-y-1.5">
              <div className="text-[10px] font-semibold text-indigo-700">{selectedSlideIds.size} slides selected</div>
              <div className="flex gap-1">
                {['morph', 'fade', 'none'].map(t => (
                  <button key={t} onClick={() => {
                    for (const sid of selectedSlideIds) {
                      metaMap.set(`${sid}:transition`, t);
                    }
                    syncSlides();
                  }} className={`flex-1 px-1 py-1 text-[9px] font-medium rounded ${
                    t === 'morph' ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    : t === 'fade' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>{t === 'none' ? 'No Trans' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
                ))}
              </div>
              <button onClick={() => {
                setDeleteConfirm(`Delete ${selectedSlideIds.size} slide${selectedSlideIds.size > 1 ? 's' : ''}? This cannot be undone.`);
              }} className="w-full px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100">
                Delete {selectedSlideIds.size} slides
              </button>
              <button onClick={() => {
                const indices = [...selectedSlideIds].map(sid => slides.findIndex(s => s.id === sid)).filter(i => i >= 0);
                const count = copySlides(indices);
                if (count > 0) setSelectedSlideIds(new Set());
              }} className="w-full px-2 py-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100">
                Copy {selectedSlideIds.size} slides
              </button>
              <button onClick={() => setSelectedSlideIds(new Set())} className="w-full px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">
                Clear selection
              </button>
            </div>
          )}

          {slides.map((slide, i) => {
            const isSelected = selectedSlideIds.has(slide.id);
            const isActive = activeSlide === i;
            return (
              <div key={slide.id}
                className={`relative group cursor-pointer rounded-lg border-2 transition-colors ${
                  isActive ? 'border-orange-500 shadow-md'
                  : isSelected ? 'border-indigo-400 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={(e) => {
                  if (e.shiftKey && slides.length > 1) {
                    const from = Math.min(activeSlide, i);
                    const to = Math.max(activeSlide, i);
                    const newSet = new Set(selectedSlideIds);
                    for (let j = from; j <= to; j++) newSet.add(slides[j].id);
                    setSelectedSlideIds(newSet);
                  } else if (e.metaKey || e.ctrlKey) {
                    const newSet = new Set(selectedSlideIds);
                    if (newSet.has(slide.id)) newSet.delete(slide.id);
                    else newSet.add(slide.id);
                    setSelectedSlideIds(newSet);
                  } else {
                    setActiveSlide(i);
                    setSelectedShapeId(null);
                    setSelectedShapeIds(new Set());
                    setSelectedSlideIds(new Set());
                  }
                }}>
                <SlideThumbnail slideId={slide.id} bg={slide.bg} getShapesMap={getShapesMap} index={i} />
                <span className="absolute top-0.5 left-1 text-[9px] text-gray-400 font-medium">{i + 1}</span>
                {isSelected && (
                  <div className="absolute top-0.5 left-1 w-3.5 h-3.5 bg-indigo-500 rounded-sm flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                )}
                {slides.length > 1 && (
                  <button onClick={e => { e.stopPropagation(); deleteSlide(i); }}
                    className="absolute top-0.5 right-0.5 hidden group-hover:block text-red-400 hover:text-red-600 p-0.5 z-10">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
                {i > 0 && !isSelected && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      const types = ['none', 'morph', 'fade'];
                      const idx = types.indexOf(slide.transition || 'none');
                      metaMap.set(`${slide.id}:transition`, types[(idx + 1) % types.length]);
                      syncSlides();
                    }}
                    className={`absolute bottom-0.5 left-1 text-[8px] px-1 py-0.5 rounded ${
                      slide.transition === 'morph' ? 'bg-purple-100 text-purple-700 font-semibold'
                      : slide.transition === 'fade' ? 'bg-blue-100 text-blue-700 font-semibold'
                      : 'hidden group-hover:block bg-gray-100 text-gray-400'
                    }`}
                    title={`Transition: ${slide.transition || 'none'} — click to cycle`}
                  >
                    {slide.transition === 'morph' ? 'Morph' : slide.transition === 'fade' ? 'Fade' : '+ Trans'}
                  </button>
                )}
              </div>
            );
          })}
          <button onClick={() => addSlide(activeSlide)} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 text-xs hover:border-gray-400 hover:text-gray-500">
            + Add slide
          </button>
          {localStorage.getItem('jubly_slide_clipboard') && (
            <button onClick={() => pasteSlides(activeSlide)} className="w-full py-1.5 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg">
              Paste slides
            </button>
          )}
        </div>

        {/* Canvas + notes */}
        <div className="flex-1 flex flex-col min-h-0">
          <SlideCanvas
            shapes={shapes}
            selectedId={selectedShapeId}
            selectedIds={selectedShapeIds}
            onSelect={(id, multi) => {
              setSelectedShapeId(id);
              if (multi && id) {
                setSelectedShapeIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              } else {
                setSelectedShapeIds(id ? new Set([id]) : new Set());
              }
            }}
            onUpdate={updateShape}
            onDelete={deleteShape}
            onAdd={addShape}
            bg={slides[activeSlide]?.bg}
            showSlideNumbers={showSlideNumbers}
            slideNumber={activeSlide + 1}
            totalSlides={slides.length}
          />

          <div className="hidden sm:block border-t border-gray-200 bg-white px-6 py-2 flex-shrink-0">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Speaker Notes</div>
            <textarea value={speakerNotes} onChange={e => updateNotes(e.target.value)}
              className="w-full h-16 text-sm text-gray-600 resize-none border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-orange-300"
              placeholder="Add notes for this slide..." />
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Slides</h3>
              <p className="text-sm text-gray-600">{deleteConfirm}</p>
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => {
                const idsToDelete = new Set(selectedSlideIds);
                const arr = [];
                for (let i = 0; i < slidesArray.length; i++) arr.push(slidesArray.get(i));
                ydoc.transact(() => {
                  if (idsToDelete.size >= arr.length) {
                    const newId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                    slidesArray.push([newId]);
                    metaMap.set(`${newId}:bg`, '#ffffff');
                  }
                  for (let i = arr.length - 1; i >= 0; i--) {
                    if (idsToDelete.has(arr[i])) slidesArray.delete(i, 1);
                  }
                });
                setSelectedSlideIds(new Set());
                setActiveSlide(0);
                setDeleteConfirm(null);
                syncSlides();
              }} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates modal */}
      {showTemplates && (
        <TemplatesModal
          onClose={() => setShowTemplates(false)}
          onApply={applyTemplate}
        />
      )}
    </div>
  );
}
